import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Box, Button, IconButton, Chip, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { HFilledTextField } from '@ui/components/common/TextField';
import CustomAvatar from '@ui/components/common/Avatar';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { StyledMenu } from '@ui/components/common/Menu';
import { PageLayout } from '@ui/components/PageLayout';
import { centeredRowStack } from '@ui/style/style';
import { AddCircle, Profile2User } from 'iconsax-react';
import { Delete } from 'react-iconly';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SearchFilter from '@ui/components/Button/SearchFilter';
import AddNewRelationDialog from './components/AddNewRelationDialog';
import AddNewRelationForm from './components/AddNewRelationForm';
import useRelationsStyles from './useRelationsStyles';
import { deleteExternalRelationAsync, getAllExternalRelationsAsync } from '@application/features/externalRelations/externalRelationsThunk';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { hasPermission } from '@application/helpers/permission-helper';
const RelationsManagement = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const styles = useRelationsStyles();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [searchValue, setSearchValue] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [isAddRelationOpen, setIsAddRelationOpen] = useState(false);
    const [editRelation, setEditRelation] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRelationId, setSelectedRelationId] = useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});
    const [anchorMoreOptionEl, setAnchorMoreOptionEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState();
    const formRef = useRef(null);
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { externalRelations, externalRelation } = useSelector((state) => state.externalRelations);
    const { interestswithoutpagination } = useSelector((state) => state.interests);
    const { peopleForTasks } = useSelector((state) => state.people);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canReadExternalRelations = hasPermission(loggedInUserPermissions, 'Permissions.ExternalRelation.Read');
    const canCreateExternalRelations = hasPermission(loggedInUserPermissions, 'Permissions.ExternalRelation.Create');
    const canUpdateExternalRelations = hasPermission(loggedInUserPermissions, 'Permissions.ExternalRelation.Update');
    const canDeleteExternalRelations = hasPermission(loggedInUserPermissions, 'Permissions.ExternalRelation.Delete');
    const dispatch = useAppDispatch();
    const getAllExternalRelation = () => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue,
                personId: filters.referredBy,
                contact: filters.contact,
                company: filters.company,
                name: filters.name,
                recentEvent: filters.recentEvent
            };
            dispatch(getAllExternalRelationsAsync(request));
        }
    };
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            getAllExternalRelation();
        }
    }, [
        selectedCompanyId,
        paginationModel,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        externalRelation,
        filters
    ]);
    // Mock data - replace with actual API call
    // const mockRelations: Relation[] = [
    //   {
    //     id: '1',
    //     contact: 'Jasper van Dijk',
    //     role: 'CTO',
    //     company: 'Array1',
    //     recentEvent: 'Music concert',
    //     referredBy: 'Sophie de Boer',
    //     referredByCompany: 'VDL Group'
    //   },
    //   {
    //     id: '2',
    //     contact: 'Lian Vermeulen',
    //     role: 'Project Manager',
    //     company: 'Prime Business co.',
    //     recentEvent: 'AFC Ajax match',
    //     referredBy: 'Emma Abrahams',
    //     referredByCompany: 'Smit Elektrotechniek'
    //   },
    //   {
    //     id: '3',
    //     contact: 'Brian van den Berg',
    //     role: 'Business Analyst',
    //     company: 'MarielTS',
    //     recentEvent: 'Formula 2 race',
    //     referredBy: 'Daan van Leeuwen',
    //     referredByCompany: 'Baars'
    //   },
    //   {
    //     id: '4',
    //     contact: 'Huub Brok',
    //     role: 'CEO',
    //     company: 'Array1',
    //     recentEvent: 'Music concert',
    //     referredBy: 'Arno de Groot',
    //     referredByCompany: 'VDL Group'
    //   },
    //   {
    //     id: '5',
    //     contact: 'Tim van der Meer',
    //     role: 'CTO',
    //     company: 'Prime Business co.',
    //     recentEvent: 'Music concert',
    //     referredBy: 'Laura van Dongen',
    //     referredByCompany: 'VDL Group'
    //   },
    //   {
    //     id: '6',
    //     contact: 'Fiona van Rooijen',
    //     role: 'Project Manager',
    //     company: 'MarielTS',
    //     recentEvent: 'AFC Ajax match',
    //     referredBy: 'Kiki van der Linden',
    //     referredByCompany: 'B.V. include co.'
    //   },
    //   {
    //     id: '7',
    //     contact: 'Ron de Vries',
    //     role: 'Business Analyst',
    //     company: 'Array1',
    //     recentEvent: 'Music concert',
    //     referredBy: 'Lotte van der Wal',
    //     referredByCompany: 'Baars'
    //   }
    // ];
    // const filteredRelations = useMemo(() => {
    //   return mockRelations.filter((relation) => {
    //     // Search filter
    //     if (searchValue) {
    //       const searchLower = searchValue.toLowerCase();
    //       const matchesSearch =
    //         relation.contact.toLowerCase().includes(searchLower) ||
    //         relation.company.toLowerCase().includes(searchLower) ||
    //         relation.recentEvent.toLowerCase().includes(searchLower) ||
    //         relation.referredBy.toLowerCase().includes(searchLower) ||
    //         relation.role.toLowerCase().includes(searchLower) ||
    //         relation.referredByCompany.toLowerCase().includes(searchLower);
    //       if (!matchesSearch) return false;
    //     }
    //     // Column filters - matching form fields: name, role, company, referredBy, interests
    //     if (filters.name && !relation.contact.toLowerCase().includes(filters.name.toLowerCase())) {
    //       return false;
    //     }
    //     if (filters.role && relation.role !== filters.role) {
    //       return false;
    //     }
    //     if (filters.company && relation.company !== filters.company) {
    //       return false;
    //     }
    //     if (filters.referredBy && relation.referredBy !== filters.referredBy) {
    //       return false;
    //     }
    //     // Note: interests filter would need to be added to the relation data model
    //     // For now, we'll skip it if the field doesn't exist in the data
    //     return true;
    //   });
    // }, [searchValue, filters]);
    const rows = externalRelations?.items.map((relation) => ({
        id: relation.id,
        userProfilePic: '',
        fullName: `${relation.firstName} ${relation.lastName}`,
        email: relation.email,
        phonenumber: relation.phoneNo,
        designation: relation.designation,
        company: relation.company,
        recentEvent: relation.recentEvent,
        personName: relation.personName,
        personCompany: relation.personCompany,
        interests: relation.interests
    }));
    const handleMoreClick = (event, row) => {
        event.stopPropagation();
        setAnchorMoreOptionEl(event.currentTarget);
        setSelectedRow(row);
    };
    const handleCloseMenu = () => {
        setAnchorMoreOptionEl(null);
    };
    const handleEditClick = (relationId) => {
        if (!canUpdateExternalRelations) {
            toast.error(t('common.unauthorized') || 'You do not have permission to edit external relations');
            return;
        }
        if (!relationId)
            return;
        setSelectedRelationId(relationId);
        setEditRelation(true);
        if (isSmallScreen) {
            setDrawerOpen(true);
        }
        else {
            setIsAddRelationOpen(true);
        }
    };
    const handleDeleteClick = (relationId) => {
        if (!canDeleteExternalRelations) {
            toast.error(t('common.unauthorized') || 'You do not have permission to delete external relations');
            return;
        }
        if (!relationId)
            return;
        setSelectedRelationId(relationId);
        setOpenDeleteConfirmation(true);
    };
    const handleDelete = () => {
        if (!selectedRelationId)
            return;
        dispatch(deleteExternalRelationAsync(selectedRelationId))
            .unwrap()
            .then(() => {
            getAllExternalRelation();
            toast.success(t('relations.relationDeletedSuccessfully') || 'Relation deleted successfully');
        })
            .catch(() => {
            toast.error(t('relations.relationDeletedFail') || 'Relation deleted successfully');
        });
        setOpenDeleteConfirmation(false);
        setSelectedRelationId(null);
    };
    const moreMenuOptions = [
        {
            text: t('tasks.edit') || 'Edit',
            icon: _jsx(Icon, { icon: 'eva:edit-outline', fontSize: 20 }),
            disabled: !canUpdateExternalRelations,
            to: () => {
                if (canUpdateExternalRelations) {
                    handleEditClick(selectedRow?.id);
                    handleCloseMenu();
                }
            }
        },
        {
            text: t('tasks.delete') || 'Delete',
            icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
            color: theme.palette.error.light,
            disabled: !canDeleteExternalRelations,
            to: () => {
                if (canDeleteExternalRelations) {
                    handleDeleteClick(selectedRow?.id);
                    handleCloseMenu();
                }
            }
        }
    ];
    const relationsColumns = [
        {
            field: 'fullname',
            headerName: t('relations.fullName'),
            flex: 1,
            minWidth: 280,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: '100%',
                        width: '100%'
                    }, children: [_jsx(CustomAvatar, { src: params.row.userProfilePic, name: params.row.fullName }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                minWidth: 0,
                                flex: 1
                            }, children: [_jsx(Typography, { variant: 'body2', noWrap: true, style: { overflow: 'hidden', maxWidth: '100%', textOverflow: 'ellipsis' }, children: params.row.fullName }), _jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.secondary }, noWrap: true, style: { overflow: 'hidden', maxWidth: '100%', textOverflow: 'ellipsis' }, children: params.row.designation })] })] }));
            }
        },
        {
            field: 'contact',
            headerName: t('relations.contact'),
            flex: 1,
            minWidth: 280,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: '100%',
                        width: '100%'
                    }, children: _jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            minWidth: 0,
                            flex: 1
                        }, children: [_jsx(Typography, { variant: 'body2', noWrap: true, style: { overflow: 'hidden', maxWidth: '100%', textOverflow: 'ellipsis' }, children: params.row.email }), params.row.phonenumber && (_jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.secondary }, noWrap: true, style: { overflow: 'hidden', maxWidth: '100%', textOverflow: 'ellipsis' }, children: '+' + params.row.phonenumber }))] }) }));
            }
        },
        {
            field: 'company',
            headerName: t('relations.company'),
            flex: 1,
            align: 'left',
            minWidth: 180,
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: _jsx(Typography, { variant: 'body2', children: params.row.company || '-' }) }));
            }
        },
        {
            field: 'recentEvent',
            headerName: t('relations.recentEvent'),
            flex: 1,
            align: 'left',
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%',
                        flex: 1,
                        minWidth: 0
                    }, children: _jsx(Typography, { variant: 'body2', noWrap: true, style: { overflow: 'hidden', maxWidth: '100%', textOverflow: 'ellipsis' }, children: params.value || '-' }) }));
            }
        },
        {
            field: 'interests',
            headerName: t('relations.tags'),
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                const interests = params.row.interests;
                if (!interests || !Array.isArray(interests) || interests.length === 0) {
                    return _jsx("span", { children: "-" });
                }
                return (_jsx(Tooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: interests.map((interest, index) => (_jsx(Typography, { variant: 'body2', sx: { py: 0.25 }, children: interest.title }, index))) }), arrow: true, placement: 'bottom', children: _jsx(Chip, { label: String(interests.length).padStart(2, '0'), size: 'small', sx: { fontWeight: 400, borderRadius: '500px', cursor: 'pointer' }, variant: 'soft', color: 'default' }) }));
            }
        },
        {
            field: 'referredBy',
            headerName: t('relations.referredBy'),
            flex: 1,
            align: 'left',
            minWidth: 180,
            sortable: false,
            renderCell: (params) => {
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: '100%'
                    }, children: [params.row.personName && _jsx(CustomAvatar, { src: '', name: params.row.personName }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                minWidth: 0,
                                flex: 1
                            }, children: [_jsx(Typography, { variant: 'body2', noWrap: true, children: params.row.personName || '-' }), _jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.secondary }, noWrap: true, children: params.row.personCompany })] })] }));
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => {
                // Check if user has any permissions to perform actions on this row
                const hasActions = canUpdateExternalRelations || canDeleteExternalRelations;
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%'
                    }, onClick: (e) => e.stopPropagation(), children: _jsx(Tooltip, { title: t('actions.more') || 'More', arrow: true, children: _jsx(IconButton, { size: 'medium', color: 'inherit', disabled: !hasActions, onClick: (e) => {
                                if (hasActions) {
                                    handleMoreClick(e, params.row);
                                }
                            }, children: _jsx(Icon, { icon: 'eva:more-horizontal-fill' }) }) }) }));
            }
        }
    ];
    const handleSearchChange = (value) => {
        setSearchValue(value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };
    const handleFilterClick = () => {
        setTempFilters(filters);
        setOpenFilterDialog(true);
    };
    // Get unique interests for filter - using same options as form
    const referredByOptions = peopleForTasks?.map((people) => ({
        label: `${people.name} (${people.peopleCompany})`,
        value: people.id
    })) ?? [];
    const renderFilterInput = (column) => {
        const handleChange = (value) => {
            setTempFilters((prev) => ({
                ...prev,
                [column.field]: value
            }));
        };
        // Name field - text input (matching form field)
        if (column.field === 'name') {
            return (_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: t('relations.name'), fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        if (column.field === 'contact') {
            return (_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: t('relations.contact'), fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        // Company field - dropdown
        if (column.field === 'company') {
            return (_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: t('relations.company'), fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        // Recent Event field - text field
        if (column.field === 'recentEvent') {
            return (_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: column.headerName, fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        // Referred By field - dropdown (matching form field)
        if (column.field === 'referredBy') {
            const selectedOption = referredByOptions.find((opt) => opt.value === tempFilters[column.field]) ||
                null;
            return (_jsx(Autocomplete, { options: referredByOptions, value: selectedOption, onChange: (_, newValue) => {
                    handleChange(newValue?.value ?? null);
                }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, renderInput: (params) => (_jsx(HFilledTextField, { ...params, label: t('relations.referredBy'), fullWidth: true, bg: 'grey8' })), fullWidth: true }));
        }
        // Default for string fields
        if (column.type === 'string' || !column.type) {
            return (_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: column.headerName, fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        return null;
    };
    const renderAllColumnFilters = () => {
        // Filter fields matching the form: name, role, company, referredBy, interests
        const filterFields = [
            { field: 'name', headerName: t('relations.name') },
            { field: 'contact', headerName: t('relations.contact') },
            { field: 'company', headerName: t('relations.company') },
            { field: 'recentEvent', headerName: t('relations.recentEvent') },
            { field: 'referredBy', headerName: t('relations.referredBy') }
        ];
        return (_jsx(Box, { display: 'flex', flexDirection: 'column', gap: 2, p: 1, children: filterFields.map((field) => (_jsx(Box, { children: renderFilterInput({
                    field: field.field,
                    headerName: field.headerName,
                    type: 'string'
                }) }, field.field))) }));
    };
    return (_jsxs(PageLayout, { title: t('common.people') || 'People', secondTitle: t('people.accessAndManageAllPeopleRecordsHere'), showLeftIcon: true, leftIcon: _jsx(Profile2User, { variant: 'Outline' }), children: [_jsx(Box, { sx: { ...styles.relationsContainer }, children: _jsx(PrimaryGrid, { rows: rows, haveSearch: false, showFilters: false, columns: relationsColumns, showNewHeaderControls: true, customButton: _jsxs(Box, { sx: { ...centeredRowStack, justifyContent: 'flex-end', pb: '12px' }, gap: 1, children: [_jsx(SearchFilter, { searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('placeholders.search') || 'Search...', onFilterClick: handleFilterClick, filterLabel: t('events.filters') || 'Filters', breakpoint: 'lg', onClear: () => {
                                    setSearchValue('');
                                    setFilters({});
                                }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                    setFilters({});
                                    setTempFilters({});
                                }, isDatagrid: true, widthBox: '50%', positonTop: '-9px' }), _jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: isSmallScreen ? 'medium' : 'medium', color: 'primary', disabled: !canCreateExternalRelations, onClick: () => {
                                    setEditRelation(false);
                                    setSelectedRelationId(null);
                                    if (isSmallScreen) {
                                        setDrawerOpen(true);
                                    }
                                    else {
                                        setIsAddRelationOpen(true);
                                    }
                                }, children: t('relations.newRelation') || 'New Relation' })] }), maxHeight: 'calc(100vh - 180px)', paginationMode: 'server', noRowsText: t('dataGrid.noRowsAdd', {
                        entityPlural: (t('relations.relations') || 'relations').toLowerCase(),
                        entitySingular: (t('relations.relation') || 'relation').toLowerCase()
                    }), paginationModel: paginationModel, rowCount: externalRelations?.totalCount, loading: false, onPaginationModelChange: setPaginationModel, disableRowSelectionOnClick: true, onRowClick: (params) => navigate(`/relations/${params.id}`) }) }), isSmallScreen ? (_jsx(_Fragment, { children: _jsx(BDrawer, { anchor: 'bottom', title: editRelation
                        ? t('relations.editRelation') || t('relations.editRelation')
                        : t('relations.addNewRelation'), open: drawerOpen, onClose: () => {
                        setEditRelation(false);
                        setDrawerOpen(false);
                        setSelectedRelationId(null);
                    }, dialogActions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                    setDrawerOpen(false);
                                    setEditRelation(false);
                                    setSelectedRelationId(null);
                                }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                    if (formRef.current) {
                                        formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                    }
                                }, children: editRelation ? t('actions.saveChanges') : t('relations.addNew') })] }), children: _jsx(AddNewRelationForm, { onClose: () => {
                            setDrawerOpen(false);
                            setEditRelation(false);
                            setSelectedRelationId(null);
                            getAllExternalRelation();
                        }, formRef: formRef, edit: editRelation, relationId: selectedRelationId }) }) })) : (_jsx(Box, { children: isAddRelationOpen && (_jsx(AddNewRelationDialog, { open: isAddRelationOpen, edit: editRelation, relationId: selectedRelationId, onClose: () => {
                        setIsAddRelationOpen(false);
                        setEditRelation(false);
                        setSelectedRelationId(null);
                    } })) })), openDeleteConfirmation &&
                (isSmallScreen ? (_jsx(BDrawer, { open: openDeleteConfirmation, onClose: () => {
                        setOpenDeleteConfirmation(false);
                        setSelectedRelationId(null);
                    }, title: t('common.deleteConfirmation'), dialogActions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => {
                                    setOpenDeleteConfirmation(false);
                                    setSelectedRelationId(null);
                                }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDelete, sx: {
                                    backgroundColor: theme.palette.error.main
                                }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => {
                        setOpenDeleteConfirmation(false);
                        setSelectedRelationId(null);
                    }, actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => {
                                        setOpenDeleteConfirmation(false);
                                        setSelectedRelationId(null);
                                    }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDelete, sx: {
                                        backgroundColor: theme.palette.error.main
                                    }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) }))), isSmallScreen ? (_jsx(BDrawer, { anchor: 'bottom', title: t('users.filters') || 'Filters', filterLabel: t('actions.clearFilters'), open: openFilterDialog, dialogActions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                setTempFilters(filters); // Reset temp filters to current filters
                                setOpenFilterDialog(false);
                            }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                // Remove keys with null, undefined, or empty string values
                                const cleanedFilters = Object.fromEntries(Object.entries(tempFilters).filter(([_, value]) => {
                                    return value !== null && value !== undefined && value !== '';
                                }));
                                setFilters(cleanedFilters); // Apply cleaned filters
                                setOpenFilterDialog(false);
                            }, children: t('actions.applyFilters') })] }), onClose: () => {
                    setTempFilters(filters); // Reset temp filters to current filters on close
                    setOpenFilterDialog(false);
                }, showClearFilterButton: Object.keys(filters).length > 0, handleClearFilter: () => {
                    setFilters({});
                    setTempFilters({});
                    setOpenFilterDialog(false);
                }, children: _jsx(Box, { sx: {
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        flex: 1
                    }, children: renderAllColumnFilters() }) })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: openFilterDialog, title: t('users.filters') || 'Filters', onClose: () => {
                    setTempFilters(filters); // Reset temp filters to current filters on close
                    setOpenFilterDialog(false);
                }, actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                setTempFilters(filters); // Reset temp filters to current filters
                                setOpenFilterDialog(false);
                            }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                // Remove keys with null, undefined, or empty string values
                                const cleanedFilters = Object.fromEntries(Object.entries(tempFilters).filter(([_, value]) => {
                                    return value !== null && value !== undefined && value !== '';
                                }));
                                setFilters(cleanedFilters); // Apply cleaned filters
                                setOpenFilterDialog(false);
                            }, children: t('actions.applyFilters') })] }), children: _jsx(Box, { sx: {
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }, children: renderAllColumnFilters() }) })), anchorMoreOptionEl && (_jsx(StyledMenu, { anchorEl: anchorMoreOptionEl, open: Boolean(anchorMoreOptionEl), onClose: handleCloseMenu, actions: moreMenuOptions }))] }));
};
export default RelationsManagement;
